import { useEffect, useState } from "react";
import { api, fileUrl, authedFileUrl } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, ImageIcon, Download, ChevronLeft, X } from "lucide-react";

export default function ParentPhotos() {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        api.get("/albums").then((r) => setAlbums(r.data.albums)).finally(() => setLoading(false));
    }, []);

    const openAlbum = async (album) => {
        setSelected(album);
        const r = await api.get(`/albums/${album.id}/photos`);
        setPhotos(r.data.photos);
    };

    const downloadPhoto = async (photo) => {
        const url = `${photo.image_url}/download`;
        const a = document.createElement("a");
        a.href = authedFileUrl(url);
        a.download = `${photo.caption || "photo"}.jpg`;
        a.click();
    };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    if (selected) {
        return (
            <div className="space-y-6" data-testid="parent-album-view">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <Button variant="ghost" onClick={() => setSelected(null)} className="rounded-full -ml-3" data-testid="back-to-albums-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" /> All Albums
                        </Button>
                        <h1 className="font-heading text-3xl font-black tracking-tight mt-2">{selected.album_name}</h1>
                        <p className="text-muted-foreground text-sm">{photos.length} photos</p>
                    </div>
                </div>
                {photos.length === 0 ? (
                    <EmptyAlbumState />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {photos.map((p) => (
                            <div key={p.id} className="group relative aspect-square rounded-2xl overflow-hidden bg-muted cursor-pointer" onClick={() => setLightbox(p)} data-testid={`photo-${p.id}`}>
                                <img src={authedFileUrl(p.image_url)} alt={p.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                {p.caption && (
                                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-xs">
                                        {p.caption}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {lightbox && (
                    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
                        <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white" data-testid="lightbox-close-btn">
                            <X className="h-6 w-6" />
                        </button>
                        <div className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
                            <img src={authedFileUrl(lightbox.image_url)} alt={lightbox.caption} className="max-h-[80vh] rounded-2xl" />
                            <div className="flex items-center justify-between mt-4 text-white">
                                <div className="text-sm">{lightbox.caption}</div>
                                <Button onClick={() => downloadPhoto(lightbox)} className="rounded-full" data-testid="lightbox-download-btn">
                                    <Download className="h-4 w-4 mr-2" /> Download
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="parent-photos-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Memories</div>
                <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Photo Albums</h1>
                <p className="text-muted-foreground mt-1">Browse and download memorable moments.</p>
            </div>
            {albums.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                    <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No albums yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {albums.map((a) => (
                        <Card
                            key={a.id}
                            className="rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all"
                            onClick={() => openAlbum(a)}
                            data-testid={`album-${a.id}`}
                        >
                            <div className="aspect-[4/3] bg-muted overflow-hidden">
                                {a.thumbnail ? (
                                    <img src={authedFileUrl(a.thumbnail)} alt={a.album_name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                        <ImageIcon className="h-10 w-10" />
                                    </div>
                                )}
                            </div>
                            <CardContent className="p-4">
                                <div className="font-bold font-heading">{a.album_name}</div>
                                <div className="text-xs text-muted-foreground mt-1">{a.photo_count} photos</div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

const EmptyAlbumState = () => (
    <div className="text-center py-16 border border-dashed border-border rounded-3xl">
        <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No photos in this album yet.</p>
    </div>
);
